import React, { useState, useEffect, useCallback } from 'react';
import { composeOutlookEmail, listClients, listFirmUsers, getEmailSignature } from '../api';

function ComposeEmail({ authAxios, showFormMessage, onClose, caseId = null, defaultTo = '' }) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [signature, setSignature] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clientData, teamData, sigData] = await Promise.all([
          listClients(authAxios).catch(() => []),
          listFirmUsers(authAxios).catch(() => []),
          getEmailSignature(authAxios).catch(() => ({ email_signature: '' })),
        ]);
        if (cancelled) return;
        setClients(Array.isArray(clientData) ? clientData : []);
        setTeamMembers(Array.isArray(teamData) ? teamData : []);
        setSignature(sigData.email_signature || '');
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [authAxios]);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments(prev => [...prev, { name: file.name, content_type: file.type || 'application/octet-stream', content_bytes: base64 }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, []);

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleClientSelect = (email) => {
    if (!email) return;
    const current = to.trim();
    setTo(current ? `${current}, ${email}` : email);
  };

  const handleTeamCcSelect = (email) => {
    if (!email) return;
    const current = cc.trim();
    setCc(current ? `${current}, ${email}` : email);
  };

  const handleSend = async () => {
    const toList = to.split(/[,;\s]+/).filter(Boolean);
    if (toList.length === 0) {
      showFormMessage('error', 'At least one recipient is required.');
      return;
    }
    if (!subject.trim()) {
      showFormMessage('error', 'Subject is required.');
      return;
    }

    setIsSending(true);
    try {
      const ccList = cc ? cc.split(/[,;\s]+/).filter(Boolean) : [];
      await composeOutlookEmail(authAxios, {
        to: toList,
        cc: ccList,
        subject: subject.trim(),
        body_html: bodyHtml || '<p></p>',
        case_id: caseId || null,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      showFormMessage('success', 'Email sent successfully.');
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send email.';
      showFormMessage('error', detail);
    } finally {
      setIsSending(false);
    }
  };

  // Build list of client emails for dropdown
  const clientEmails = clients
    .map(c => ({
      id: c.id,
      name: c.name || c.full_name ||
            ((c.first_name || c.last_name) ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : null) ||
            `Client #${c.id}`,
      email: c.email,
    }))
    .filter(c => c.email);

  // Build list of team member emails — StaffProfileSerializer returns first_name/last_name/email flat
  const teamEmails = teamMembers
    .map(t => ({
      id: t.id,
      name: t.full_name ||
            ((t.first_name || t.last_name) ? `${t.first_name || ''} ${t.last_name || ''}`.trim() : null) ||
            t.username ||
            `User #${t.id}`,
      email: t.email || t.user?.email,
    }))
    .filter(t => t.email);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">Compose Email</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* To field with client dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="email@example.com, another@example.com"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              />
              {clientEmails.length > 0 && (
                <select
                  onChange={(e) => { handleClientSelect(e.target.value); e.target.value = ''; }}
                  defaultValue=""
                  className="border border-gray-300 rounded px-2 py-2 text-sm text-gray-600 w-44"
                >
                  <option value="">+ Client</option>
                  {clientEmails.map(c => (
                    <option key={c.id} value={c.email}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* CC field with team dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CC</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Optional"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              />
              {teamEmails.length > 0 && (
                <select
                  onChange={(e) => { handleTeamCcSelect(e.target.value); e.target.value = ''; }}
                  defaultValue=""
                  className="border border-gray-300 rounded px-2 py-2 text-sm text-gray-600 w-44"
                >
                  <option value="">+ Team Member</option>
                  {teamEmails.map(t => (
                    <option key={t.id} value={t.email}>{t.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              placeholder="Type your message..."
            />
          </div>

          {/* Signature preview */}
          {signature && (
            <div className="border-t border-gray-200 pt-3">
              <p className="text-xs text-gray-500 mb-1">Signature (appended automatically)</p>
              <div className="text-xs text-gray-600 bg-gray-50 rounded p-2 max-h-20 overflow-y-auto" dangerouslySetInnerHTML={{ __html: signature }} />
            </div>
          )}

          {/* File attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1">
                {attachments.map((att, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="truncate max-w-xs">{att.name}</span>
                    <button onClick={() => removeAttachment(idx)} className="text-red-500 hover:text-red-700 text-xs">&times;</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isSending}
            className="px-6 py-2 bg-blue-600 text-white font-semibold text-sm rounded shadow hover:bg-blue-700 disabled:opacity-50"
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ComposeEmail;
